import React, { useEffect, useState, useMemo } from "react";
import { doc, getDoc, setDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useParams, useNavigate } from "react-router-dom";

function normalizeDaily(amount, frequency){
  if (frequency === "daily") return amount;
  if (frequency === "weekly") return amount / 7;
  if (frequency === "monthly") return amount / 30.4375;
  if (frequency === "yearly") return amount / 365.25;
  return 0;
}

export default function JobFinancials(){
  const { id } = useParams(); // jobId
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [fin, setFin] = useState({
    priceSource: "manual",
    price: 0,
    materialsCost: 0,
    laborCost: 0,
    miscCost: 0,
    overheadAllocMethod: "split_evenly",
    overheadAllocValue: 0,
    overheadOverride: null,
    jobDate: ""
  });
  const [dailyOverhead, setDailyOverhead] = useState(0);
  const [jobsOnDate, setJobsOnDate] = useState(1);

  // load job + financial doc
  useEffect(() => {
    (async () => {
      const jobRef = doc(db, "jobs", id);
      const jobSnap = await getDoc(jobRef);
      if (jobSnap.exists()){
        const j = { id: jobSnap.id, ...jobSnap.data() };
        setJob(j);
        const dateISO = (j.startDate && j.startDate.length === 10) ? j.startDate : "";
        // load financials
        const finRef = doc(db, "jobs", id, "financials", "v1");
        const finSnap = await getDoc(finRef);
        if (finSnap.exists()){
          setFin(prev => ({ ...prev, ...finSnap.data(), jobDate: finSnap.data().jobDate || dateISO }));
        } else {
          setFin(prev => ({ ...prev, jobDate: dateISO }));
        }

        // compute daily overhead for the date from overheadSettings
        if (dateISO){
          const osRef = collection(db, "overheadSettings");
          const osSnap = await getDocs(osRef);
          let perDay = 0;
          osSnap.forEach(d => {
            const o = d.data();
            if (!o.active) return;
            if (o.startDate && o.startDate > dateISO) return;
            if (o.endDate && o.endDate < dateISO) return;
            perDay += normalizeDaily(o.amount, o.frequency);
          });
          setDailyOverhead(perDay);

          // count jobs on that date for split-evenly
          const allJobsSnap = await getDocs(collection(db, "jobs"));
          let count = 0;
          allJobsSnap.forEach(x => {
            const data = x.data();
            if (data.status === "Active" || data.status === "Completed"){ // include both if you want historical allocation
              if (data.startDate === dateISO) count++;
            }
          });
          setJobsOnDate(Math.max(count, 1));
        }
      }
    })();
  }, [id]);

  const allocatedOverhead = useMemo(() => {
    if (fin.overheadOverride !== null && fin.overheadOverride !== undefined){
      const v = Number(fin.overheadOverride) || 0;
      return v < 0 ? 0 : v;
    }
    if (fin.overheadAllocMethod === "split_evenly"){
      return dailyOverhead / Math.max(jobsOnDate, 1);
    }
    if (fin.overheadAllocMethod === "flat"){
      return Math.max(Number(fin.overheadAllocValue) || 0, 0);
    }
    if (fin.overheadAllocMethod === "percent"){
      const p = Math.max(Number(fin.overheadAllocValue) || 0, 0);
      return (p / 100) * (Number(fin.price)||0);
    }
    return 0;
  }, [fin, dailyOverhead, jobsOnDate]);

  const grossProfit = useMemo(() => {
    const price = Number(fin.price)||0;
    const costs = (Number(fin.materialsCost)||0) + (Number(fin.laborCost)||0) + (Number(fin.miscCost)||0) + allocatedOverhead;
    return price - costs;
  }, [fin, allocatedOverhead]);

  const marginPct = useMemo(() => {
    const price = Number(fin.price)||0;
    return price > 0 ? (grossProfit / price) * 100 : 0;
  }, [grossProfit, fin.price]);

  const save = async () => {
    const finRef = doc(db, "jobs", id, "financials", "v1");
    const payload = {
      ...fin,
      price: Number(fin.price)||0,
      materialsCost: Number(fin.materialsCost)||0,
      laborCost: Number(fin.laborCost)||0,
      miscCost: Number(fin.miscCost)||0,
      overheadAllocValue: Number(fin.overheadAllocValue)||0,
      overheadOverride: fin.overheadOverride === "" ? null : (fin.overheadOverride===null?null:Number(fin.overheadOverride)||0),
      jobDate: fin.jobDate || (job?.startDate || "")
    };
    await setDoc(finRef, payload, { merge: true });
    alert("Financials saved.");
  };

  if (!job) return <div style={{padding:16}}>Loading…</div>;

  return (
    <div style={{padding:16, maxWidth:900, margin:"0 auto"}}>
      <h2>💵 Financials — {job.siteName} (WO #{job.wo})</h2>
      <p><strong>Date:</strong> {fin.jobDate || job.startDate || "—"}</p>

      <div className="form-container">
        <label>Price Source</label>
        <select value={fin.priceSource} onChange={e=>setFin({...fin, priceSource:e.target.value})}>
          <option value="proposal">Proposal</option>
          <option value="manual">Manual</option>
        </select>

        <label>Price (what we charge)</label>
        <input type="number" value={fin.price} onChange={e=>setFin({...fin, price:e.target.value})} />

        <label>Materials Cost</label>
        <input type="number" value={fin.materialsCost} onChange={e=>setFin({...fin, materialsCost:e.target.value})} />

        <label>Labor Cost</label>
        <input type="number" value={fin.laborCost} onChange={e=>setFin({...fin, laborCost:e.target.value})} />

        <label>Misc Cost</label>
        <input type="number" value={fin.miscCost} onChange={e=>setFin({...fin, miscCost:e.target.value})} />

        <label>Overhead Allocation Method</label>
        <select
          value={fin.overheadAllocMethod}
          onChange={e=>setFin({...fin, overheadAllocMethod:e.target.value})}
        >
          <option value="split_evenly">Split daily overhead evenly across jobs that day</option>
          <option value="flat">Flat amount per job</option>
          <option value="percent">Percent of price</option>
        </select>

        {(fin.overheadAllocMethod === "flat" || fin.overheadAllocMethod === "percent") && (
          <>
            <label>{fin.overheadAllocMethod === "flat" ? "Flat $" : "% of Price"}</label>
            <input type="number" value={fin.overheadAllocValue} onChange={e=>setFin({...fin, overheadAllocValue:e.target.value})}/>
          </>
        )}

        <label>Overhead Override (optional)</label>
        <input placeholder="leave empty for auto" type="number"
          value={fin.overheadOverride ?? ""}
          onChange={e=>setFin({...fin, overheadOverride: e.target.value})}
        />
      </div>

      <div style={{display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12, marginTop:16}}>
        <div className="card"><strong>Daily Overhead (all)</strong><div>${dailyOverhead.toFixed(2)}</div></div>
        <div className="card"><strong>Jobs That Day</strong><div>{jobsOnDate}</div></div>
        <div className="card"><strong>Allocated Overhead</strong><div>${allocatedOverhead.toFixed(2)}</div></div>
        <div className="card"><strong>Gross Profit</strong><div>${grossProfit.toFixed(2)} ({marginPct.toFixed(1)}%)</div></div>
      </div>

      <div style={{marginTop:16, display:"flex", gap:8}}>
        <button className="green-button" onClick={save}>Save</button>
        <button className="red-button" onClick={()=>navigate(-1)}>Close</button>
      </div>
    </div>
  );
}
